import { apiClient } from './apiClient';
import { Business } from './businessService';
import { AppointmentTypeShare } from './appointmentTypeShareService';
import { cacheService } from './cacheService';
import { indexedDbService } from './indexedDbService';
import { offlineService } from './offlineService';

export interface AppointmentType {
    id: string;
    name: string;
    description: string;
    color: string;
    textColor: string;
    duration: number;
    requiresLocation: boolean;
    businessId: string;
    business?: Business;
    icon?: string;
    createdByUserId: string;
    createdAt: string;
    updatedAt: string;
    shares?: AppointmentTypeShare[];
}

export interface CreateAppointmentTypeDto {
    name: string;
    description: string;
    requiresLocation: boolean;
    color: string;
    textColor: string;
    icon?: string;
}

export interface UpdateAppointmentTypeDto {
    name: string;
    description: string;
    requiresLocation: boolean;
    color: string;
    textColor: string;
    icon?: string;
}

export interface DeleteAppointmentTypeDto {
    deleteAppointments: boolean;
    transferToTypeId?: string;
}

class AppointmentTypeService {
    // Cache expiration time: 10 minutes
    private readonly CACHE_EXPIRATION = 10 * 60 * 1000;
    // In-flight request deduplication
    private appointmentTypePromises: Map<string, Promise<AppointmentType[]>> = new Map();

    /**
     * Fetches appointment types from the API and updates IndexedDB
     * Used for background revalidation
     */
    private async fetchAndCacheAppointmentTypes(businessId?: string): Promise<AppointmentType[]> {
        const cacheKey = `appointmentTypes:${businessId || 'all'}`;

        try {
            const url = businessId
                ? `/appointment-types?businessId=${businessId}`
                : '/appointment-types';

            const response = await apiClient.get<AppointmentType[]>(url);

            // Update memory cache
            cacheService.set(cacheKey, response.data, this.CACHE_EXPIRATION);

            // Store appointment types in IndexedDB for offline use
            try {
                const appointmentTypesWithBusinessId = response.data.map(appointmentType => ({
                    ...appointmentType,
                    businessId: businessId || appointmentType.businessId,
                    business: appointmentType.business
                }));
                await indexedDbService.saveAppointmentTypes(appointmentTypesWithBusinessId);
            } catch (dbError) {
                console.error('Error saving appointment types to IndexedDB:', dbError);
            }

            return response.data;
        } catch (error) {
            console.error('Error fetching appointment types:', error);
            throw error;
        }
    }

    /**
     * Revalidates appointment types in the background (stale-while-revalidate pattern)
     */
    private revalidateAppointmentTypes(businessId?: string): void {
        const dedupKey = businessId || 'all';

        // Don't start a new revalidation if one is already in progress
        if (this.appointmentTypePromises.has(dedupKey)) {
            return;
        }

        console.log(`Appointment types: Background revalidation started for ${dedupKey}`);

        const promise = this.fetchAndCacheAppointmentTypes(businessId)
            .then(data => {
                console.log(`Appointment types: Background revalidation complete for ${dedupKey}`);
                return data;
            })
            .catch(error => {
                console.error(`Appointment types: Background revalidation failed for ${dedupKey}:`, error);
                return [] as AppointmentType[];
            });

        this.appointmentTypePromises.set(dedupKey, promise);

        // Clear the promise after completion
        promise.finally(() => {
            setTimeout(() => {
                this.appointmentTypePromises.delete(dedupKey);
            }, 2000);
        });
    }

    async getAppointmentTypes(businessId?: string, forceReload: boolean = false): Promise<AppointmentType[]> {
        const cacheKey = `appointmentTypes:${businessId || 'all'}`;
        const dedupKey = businessId || 'all';

        // Check if we're offline
        const isOffline = !offlineService.getOnlineStatus();

        if (isOffline) {
            console.log('Offline mode: Getting appointment types from IndexedDB');
            try {
                return await indexedDbService.getAppointmentTypes(businessId);
            } catch (error) {
                console.error('Error getting appointment types from IndexedDB:', error);
                return [];
            }
        }

        // If force reload is requested, clear the cache
        if (forceReload) {
            cacheService.remove(cacheKey);
        }

        // Check memory cache first
        const memCached = cacheService.get<AppointmentType[]>(cacheKey);
        if (memCached && !forceReload) {
            console.log(`Appointment types: Memory cache hit for ${dedupKey}`);
            return memCached;
        }

        // Check IndexedDB for stale-while-revalidate
        try {
            const cachedFromDb = await indexedDbService.getAppointmentTypes(businessId);
            if (cachedFromDb.length > 0 && !forceReload) {
                console.log(`Appointment types: IndexedDB cache hit for ${dedupKey}, revalidating in background`);
                // Return cached data immediately, revalidate in background
                this.revalidateAppointmentTypes(businessId);
                // Also update memory cache
                cacheService.set(cacheKey, cachedFromDb, this.CACHE_EXPIRATION);
                return cachedFromDb;
            }
        } catch (dbError) {
            console.error('Error checking IndexedDB for appointment types:', dbError);
        }

        // No cache available, must wait for network
        console.log(`Appointment types: No cache, fetching from API for ${dedupKey}`);

        // Check if there's already an in-flight request
        const existingPromise = this.appointmentTypePromises.get(dedupKey);
        if (existingPromise) {
            console.log(`Appointment types: Returning existing in-flight request for ${dedupKey}`);
            return existingPromise;
        }

        // Create new request with deduplication
        const promise = this.fetchAndCacheAppointmentTypes(businessId);
        this.appointmentTypePromises.set(dedupKey, promise);

        try {
            const result = await promise;
            return result;
        } catch (error) {
            // If API request fails, try to get from IndexedDB as fallback
            try {
                const offlineTypes = await indexedDbService.getAppointmentTypes(businessId);
                console.log('Retrieved appointment types from IndexedDB:', offlineTypes);
                return offlineTypes;
            } catch (dbError) {
                console.error('Error getting appointment types from IndexedDB:', dbError);
                throw error; // Re-throw the original error
            }
        } finally {
            // Clear the promise after deduplication window
            setTimeout(() => {
                this.appointmentTypePromises.delete(dedupKey);
            }, 2000);
        }
    }

    async createAppointmentType(dto: CreateAppointmentTypeDto, businessId?: string): Promise<AppointmentType> {
        try {
            const url = businessId ? `/appointment-types?businessId=${businessId}` : '/appointment-types';
            const response = await apiClient.post<AppointmentType>(url, dto);
            
            // Invalidate cache for this business
            cacheService.remove(`appointment-types:${businessId || 'all'}`);
            
            return response.data;
        } catch (error) {
            console.error('Error creating appointment type:', error);
            throw error;
        }
    }

    async updateAppointmentType(id: string, dto: UpdateAppointmentTypeDto): Promise<AppointmentType> {
        try {
            const response = await apiClient.put<AppointmentType>(`/appointment-types/${id}`, dto);
            
            // Invalidate all appointment type caches since we don't know which business this belongs to
            cacheService.removeByPrefix('appointment-types:');
            
            return response.data;
        } catch (error) {
            console.error('Error updating appointment type:', error);
            throw error;
        }
    }

    async deleteAppointmentType(id: string, dto: DeleteAppointmentTypeDto): Promise<void> {
        try {
            await apiClient.delete(`/appointment-types/${id}`, { data: dto });
            
            // Invalidate all appointment type caches
            cacheService.removeByPrefix('appointment-types:');
        } catch (error) {
            console.error('Error deleting appointment type:', error);
            throw error;
        }
    }

    /**
     * Check if appointment types exist in IndexedDB and force reload if they don't
     */
    async ensureAppointmentTypesLoaded(businessId?: string): Promise<AppointmentType[]> {
        try {
            // First check if we have appointment types in IndexedDB
            const existingTypes = await indexedDbService.getAppointmentTypes(businessId);
            
            if (existingTypes.length === 0) {
                console.log('No appointment types found in IndexedDB, forcing reload from API');
                // Clear cache and force reload from API
                const cacheKey = `appointmentTypes:${businessId || 'all'}`;
                cacheService.remove(cacheKey);
                return await this.getAppointmentTypes(businessId, true);
            }
            
            return existingTypes;
        } catch (error) {
            console.error('Error checking appointment types in IndexedDB:', error);
            // If there's an error checking IndexedDB, force reload from API
            return await this.getAppointmentTypes(businessId, true);
        }
    }
}

export const appointmentTypeService = new AppointmentTypeService(); 